import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ApiError } from '../../abstract/api-error';
import { MarketplacePayment } from '../../core/data-models/marketplace-payment.model';
import { MarketplacePaymentService } from '../../core/data-services/marketplace-payment.service';
import { formatPaymentAcres, formatPaymentDateTime, formatPaymentMoney, shortPaymentId } from '../../shared/utils/payment-format.utils';

/**
 * Admin queue of payment rows awaiting approval.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1)
 *
 * Shows rows in `pending_admin_approval` AND `failed` status — both need
 * an admin decision (failed = prior Stripe attempt failed, awaiting
 * admin retry-or-decline). Sorted oldest first so SLA pressure is
 * visible up top.
 *
 * Per-row actions link to the detail page; bulk operations are
 * deferred to v1.5 per decision 6.
 */
@Component({
  selector: 'app-pending-approval-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  // Renders inside the layout's <div class="card"><div class="card-body">
  // — so no outer card wrapper here. Mirror the tab-content pattern used
  // by organization-detail tabs (`.tab-pane` style — content lives directly
  // inside the card-body without a nested card).
  template: `
    <div class="tab-pane">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h5 class="mb-1">Pending Approval Queue</h5>
          <small class="text-muted">
            Rows awaiting admin capture decision. Oldest first.
          </small>
        </div>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          (click)="reload()"
          [disabled]="loading">
          <i class="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>
      </div>

      @if (loading) {
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      } @else if (loadError) {
        <div class="alert alert-danger">
          {{ loadError }}
        </div>
      } @else if (payments.length === 0) {
        <div class="text-center py-5 text-muted">
          <i class="bi bi-check-circle display-4 d-block mb-2 text-success"></i>
          <p class="mb-0">No payments are awaiting approval right now.</p>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="table data-table table-hover align-middle">
            <thead>
              <tr>
                <th>Status</th>
                <th>Awaiting Since</th>
                <th>Farmer Org</th>
                <th>Pilot Org</th>
                <th class="text-end">Acres</th>
                <th class="text-end">Total</th>
                <th class="text-end">Attempts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (payment of payments; track payment.id) {
                <tr
                  class="cursor-pointer"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'Review payment ' + shortId(payment.id)"
                  [routerLink]="['/marketplace-payments/payment', payment.id]"
                  (keydown.enter)="goToPayment(payment.id)"
                  (keydown.space)="$event.preventDefault(); goToPayment(payment.id)">
                  <td>
                    @if (payment.status === 'pending_admin_approval') {
                      <span class="badge bg-warning text-dark">Pending</span>
                    } @else if (payment.status === 'failed') {
                      <span class="badge bg-danger">Failed</span>
                    } @else {
                      <span class="badge bg-secondary">{{ payment.status }}</span>
                    }
                  </td>
                  <td>{{ formatDate(payment.adminApprovalRequiredAt || payment.updatedAt) }}</td>
                  <td class="text-muted small">{{ shortId(payment.farmerOrgId) }}</td>
                  <td class="text-muted small">{{ shortId(payment.pilotOrgId) }}</td>
                  <td class="text-end">{{ formatAcres(payment.billableAcres) }}</td>
                  <td class="text-end">{{ formatMoney(payment.totalChargeAmount) }}</td>
                  <td class="text-end">{{ payment.acceptanceAttemptCount ?? '—' }}</td>
                  <td class="text-end">
                    <a
                      class="btn btn-sm btn-outline-primary"
                      [routerLink]="['/marketplace-payments/payment', payment.id]"
                      (click)="$event.stopPropagation()">
                      Review
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  `,
})
export class PendingApprovalListComponent implements OnInit {
  payments: MarketplacePayment[] = [];
  loading = true;
  loadError: string | null = null;

  constructor(
    private service: MarketplacePaymentService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  goToPayment(id: string): void {
    this.router.navigate(['/marketplace-payments/payment', id]);
  }

  reload(): void {
    this.loading = true;
    this.loadError = null;
    this.service.listPendingApproval().subscribe({
      next: (payments) => {
        this.payments = payments;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = ApiError.fromHttpError(err).message;
      },
    });
  }

  shortId(id: string | null | undefined): string {
    return shortPaymentId(id);
  }

  formatDate(value: string | Date | null | undefined): string {
    return formatPaymentDateTime(value);
  }

  formatAcres(v: number | null | undefined): string {
    return formatPaymentAcres(v, false);
  }

  formatMoney(v: number | null | undefined): string {
    return formatPaymentMoney(v);
  }
}
